import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiFileText, FiSave } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, RichTextEditor } from '../../components/common';
import { createNotice } from '../../services/api';
import { NOTICE_CATEGORIES, EMPTY_NOTICE } from './noticesConfig';
import styles from './CreateNoticePage.module.css';

const CreateNoticePage = () => {
  const { activeDerasarId } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_NOTICE);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!form.content.replace(/<[^>]+>/g, '').trim()) {
      toast.error('Content is required');
      return;
    }

    setLoading(true);
    try {
      await createNotice({
        derasarId: Number(activeDerasarId),
        title: form.title,
        content: form.content,
        category: form.category || null,
        expiryDate: form.expiryDate || null,
      });

      toast.success('Notice posted successfully');
      navigate('/derasar/notices');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post notice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader
        icon={<FiFileText />}
        title="Post New Notice"
        actions={(
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/derasar/notices')}>
            <FiArrowLeft /> Back
          </button>
        )}
      />

      <form onSubmit={handleSubmit}>
        <div className={`card ${styles.card}`}>
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Notice Details</div>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                id="title"
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Notice title"
                maxLength="255"
                required
              />
            </div>

            <div className={styles.formRow}>
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                >
                  {NOTICE_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="expiryDate">Expiry Date</label>
                <input
                  id="expiryDate"
                  type="date"
                  name="expiryDate"
                  value={form.expiryDate}
                  onChange={handleChange}
                />
                <small className={styles.helpText}>Leave blank for no expiry</small>
              </div>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <div className={styles.sectionLabel}>Content</div>

            <div className="form-group">
              <label>Content *</label>
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((prev) => ({ ...prev, content: html }))}
                placeholder="Write your notice here..."
              />
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.formActions}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => navigate('/derasar/notices')}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <FiSave /> {loading ? 'Posting...' : 'Post Notice'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateNoticePage;
